/**
 * Error Handler Hook with i18n support
 * v0.69.0: Provides user-friendly error messages with internationalization
 * v0.75.0: Displays request ID in error toasts for debugging
 */

import { useMemo } from "react";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { getLastRequestID } from "../connect";

interface ErrorInfo {
  code: string;
  symbols?: string[];
  symbol?: string;
}

/**
 * Parse error message to extract structured information
 */
function parseErrorMessage(message: string): ErrorInfo | null {
  // Ensure message is a string
  if (typeof message !== "string") {
    return null;
  }

  // Format: "invalid symbols: [AACC, BBCC] - these symbols are not found in ticker_master..."
  const invalidSymbolsMatch = message.match(/invalid symbols:\s*\[([^\]]+)\]/);
  if (invalidSymbolsMatch) {
    const symbols = invalidSymbolsMatch[1]
      .split(",")
      .map((s) => s.trim().replace(/"/g, ""))
      .filter((s) => s.length > 0);

    return {
      code: "TICKERS_INVALID",
      symbols,
    };
  }

  // Format: "Symbol XXX not found in ticker master..."
  const singleSymbolMatch = message.match(/Symbol\s+(\w+)\s+not found in ticker master/);
  if (singleSymbolMatch) {
    return {
      code: "TICKER_NOT_FOUND",
      symbol: singleSymbolMatch[1],
    };
  }

  // Fallback for ticker-related errors
  if (message.includes("ticker_master") || message.includes("not found")) {
    return {
      code: "TICKER_INVALID_GENERIC",
    };
  }

  return null;
}

/**
 * Hook to get formatted error messages with i18n support
 */
export function useErrorHandler() {
  const { t } = useTranslation();

  const getErrorMessage = useMemo(() => {
    return (error: unknown): string => {
      // Handle ConnectError with details property
      if (error && typeof error === "object" && "details" in error) {
        const details = (error as { details?: string | unknown }).details;
        const detailsStr = typeof details === "string" ? details : String(details);
        if (detailsStr) {
          return formatError(detailsStr, t);
        }
      }

      // Handle Error objects
      if (error instanceof Error) {
        return formatError(error.message, t);
      }

      // Handle string errors
      if (typeof error === "string") {
        return formatError(error, t);
      }

      // Fallback
      return t("errors.unknown");
    };
  }, [t]);

  const showError = useMemo(() => {
    return (error: unknown): void => {
      const message = getErrorMessage(error);
      console.error(error);

      // v0.75.0: Extract and display request ID in error toast
      const requestID = getLastRequestID();
      const toastMessage = requestID ? `${message} (ID: ${requestID.slice(0, 8)}...)` : message;

      // v0.69.0: Fixed dynamic import anti-pattern - use static import instead
      toast.error(toastMessage, {
        duration: 5000,
      });

      // Log request ID to console for debugging
      if (requestID && import.meta.env.DEV) {
        console.debug("[Error Handler] Request ID:", requestID, "Full error:", error);
      }
    };
  }, [getErrorMessage]);

  return { getErrorMessage, showError };
}

/**
 * Format error message with i18n support
 */
function formatError(message: string | unknown, t: (key: string, params?: Record<string, unknown>) => string): string {
  // Ensure message is a string
  const messageStr = typeof message === "string" ? message : String(message);

  const errorInfo = parseErrorMessage(messageStr);

  if (!errorInfo) {
    return messageStr;
  }

  switch (errorInfo.code) {
    case "TICKERS_INVALID":
      if (errorInfo.symbols && errorInfo.symbols.length > 0) {
        return t("errors.ticker.invalid_multiple", {
          symbols: errorInfo.symbols.join(", "),
          examples: "AAPL, TSLA",
        });
      }
      break;

    case "TICKER_NOT_FOUND":
      if (errorInfo.symbol) {
        return t("errors.ticker.not_found", {
          symbol: errorInfo.symbol,
          examples: "AAPL, TSLA",
        });
      }
      break;

    case "TICKER_INVALID_GENERIC":
      return t("errors.ticker.invalid", {
        examples: "AAPL, TSLA",
      });
  }

  return messageStr;
}

/**
 * Non-hook version for use in non-React contexts
 * Requires t function to be passed in
 */
export function formatErrorMessage(message: string, t: (key: string, params?: Record<string, unknown>) => string): string {
  return formatError(message, t);
}
